import { useEffect, useRef, useState } from 'react';
import { Car, Info, UserRound, UsersRound } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Modal } from '@/components/Shared/Modal';
import { Button } from '@/components/Shared/Button';
import { ChoiceTiles, type ChoiceTile } from '@/components/Shared/ChoiceTiles';
import type { TripStartDialogState } from '@/hooks/useTripDialogs';
import { useMyVehicleTripsStore, type TripSuggestion } from '@/store/useMyVehicleTripsStore';
import type { VehicleDto } from '@/store/useVehicleStore';
import {
  BUSINESS,
  PRIVATE,
  applyTripSuggestion,
  canConfirmTripStart,
  destinationForKind,
  parseKm,
  toTripRole,
  type TripChoiceRole,
  type TripTexts,
} from '@/utils/vehicleTrips';
import { TripDriverFields, type TripDriverValues } from './TripDriverFields';
import { UnparkedVehiclesNotice } from './UnparkedVehiclesNotice';

const SELECT_CLASS =
  'w-full h-11 px-3 rounded-md bg-white border border-slate-200 text-[15px] text-slate-900 font-mono focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50';

interface TripStartDialogProps extends TripStartDialogState {
  busy?: boolean;
}

/**
 * Start, destination and purpose as suggested for this vehicle (FAFD-68): where the driver or the
 * vehicle last arrived today, the plan, the company — until the suggestion is there, the vehicle's
 * last destination.
 */
function suggestedTexts(suggestion: TripSuggestion | null, vehicle: VehicleDto | undefined): TripTexts {
  return {
    startLocation: suggestion?.startLocation ?? vehicle?.lastDestination ?? '',
    destination: suggestion?.destination ?? '',
    purpose: suggestion?.purpose ?? '',
  };
}

/** The driver's fields start from the vehicle (its last reading) and the suggestion — a business trip by default. */
function driverValuesFor(vehicle: VehicleDto | undefined): TripDriverValues {
  return {
    odometer: vehicle?.currentOdometerKm != null ? String(vehicle.currentOdometerKm) : '',
    ...suggestedTexts(null, vehicle),
    purposeKind: BUSINESS,
  };
}

function withKind(values: TripDriverValues, kind: TripDriverValues['purposeKind'], suggested: string): TripDriverValues {
  return { ...values, purposeKind: kind, destination: destinationForKind(values.destination, kind, suggested) };
}

/** null for an empty text — the backend then keeps or prefills the field. */
const text = (value: string) => value.trim() || null;

/**
 * Starts a trip (FAFD-68, ADR-0027). Mode `clockIn`: asked at clock-in with "Fahrer",
 * "Beifahrer" and "Kein Fahrer" ("Kein Fahrer" preselected unless the day is planned with a role
 * and plate); mode `start`: "Fahrt starten" on its own, driver or passenger — also without being
 * clocked in, with a hint. A driver may not drive while a vehicle is not parked. Driven by
 * useTripDialogs.
 */
export function TripStartDialog({
  isOpen,
  mode,
  vehicles,
  initial,
  departureAt,
  notClockedIn,
  busy = false,
  onConfirm,
  onCancel,
}: TripStartDialogProps) {
  const { t } = useTranslation('vehicles');
  const driverBlocked = useMyVehicleTripsStore((s) => s.unparked.length > 0);
  const allowPrivateTrips = useMyVehicleTripsStore((s) => s.allowPrivateTrips);
  const fetchSuggestion = useMyVehicleTripsStore((s) => s.fetchSuggestion);
  const [role, setRole] = useState<TripChoiceRole>('none');
  const [vehicleId, setVehicleId] = useState('');
  const [driver, setDriver] = useState<TripDriverValues>(driverValuesFor(undefined));
  // What the fields were last filled with automatically — only those the driver left alone follow a new suggestion.
  const filled = useRef<TripTexts>(suggestedTexts(null, undefined));

  const resetDriver = (vehicle: VehicleDto | undefined) => {
    setDriver(driverValuesFor(vehicle));
    filled.current = suggestedTexts(null, vehicle);
  };

  useEffect(() => {
    if (!isOpen || !initial) return;
    setRole(initial.role);
    setVehicleId(initial.vehicleId);
    resetDriver(vehicles.find((v) => v.id === initial.vehicleId));
  }, [isOpen, initial, vehicles]);

  // The suggestion depends on the vehicle (where it was left today), so it follows the choice.
  useEffect(() => {
    if (!isOpen || vehicleId === '') return;
    let current = true;
    void fetchSuggestion(departureAt, vehicleId).then((suggestion) => {
      if (!current) return;
      const next = suggestedTexts(suggestion, vehicles.find((v) => v.id === vehicleId));
      // why: the updater runs on the next render, after `filled` already points to `next` — compare
      // against what was filled before.
      const before = filled.current;
      setDriver((d) => withKind(applyTripSuggestion(d, before, next), d.purposeKind, next.destination));
      filled.current = next;
    });
    return () => {
      current = false;
    };
  }, [isOpen, vehicleId, departureAt, vehicles, fetchSuggestion]);

  const vehicle = vehicles.find((v) => v.id === vehicleId);
  const canConfirm = canConfirmTripStart({
    role,
    vehicleId,
    odometer: driver.odometer,
    driverBlocked,
    purposeKind: driver.purposeKind,
    allowPrivateTrips,
  });

  const chooseVehicle = (id: string) => {
    setVehicleId(id);
    resetDriver(vehicles.find((v) => v.id === id));
  };

  const confirm = () => {
    const tripRole = toTripRole(role);
    if (!canConfirm) return;
    if (tripRole === null) {
      onConfirm({ trip: null });
      return;
    }
    const isDriver = role === 'driver';
    const isPrivate = driver.purposeKind === PRIVATE;
    onConfirm({
      trip: {
        vehicleId,
        role: tripRole,
        ...(isDriver
          ? {
              odometerKm: parseKm(driver.odometer),
              startLocation: text(driver.startLocation),
              destination: text(driver.destination),
              purpose: isPrivate ? null : text(driver.purpose),
              ...(allowPrivateTrips ? { purposeKind: driver.purposeKind } : {}),
            }
          : {}),
      },
    });
  };

  const roleOptions: ChoiceTile<TripChoiceRole>[] = [
    { value: 'driver', label: t('driverRole.driver'), icon: Car, disabled: driverBlocked },
    { value: 'passenger', label: t('driverRole.coDriver'), icon: UsersRound },
    ...(mode === 'clockIn' ? [{ value: 'none' as const, label: t('driverRole.none'), icon: UserRound }] : []),
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={t(`trip.startTitle.${mode}`)}
      size="sm"
      footer={
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {t('clock.cancel')}
          </Button>
          <Button onClick={confirm} disabled={busy || !canConfirm}>
            {t(`trip.startConfirm.${mode}`)}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {notClockedIn && (
          <p className="flex gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-[12px] text-sky-800">
            <Info className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={2} aria-hidden />
            <span>{t('trip.notClockedIn')}</span>
          </p>
        )}

        {driverBlocked && <UnparkedVehiclesNotice fetchOnMount={false} />}

        <ChoiceTiles legend={t('clock.roleLabel')} options={roleOptions} value={role} onChange={setRole} />

        {role !== 'none' && (
          <label className="block">
            <span className="block text-[13px] font-medium text-slate-700 mb-2">{t('clock.vehicleLabel')}</span>
            <select value={vehicleId} onChange={(e) => chooseVehicle(e.target.value)} className={SELECT_CLASS}>
              <option value="" disabled>
                {t('clock.vehiclePlaceholder')}
              </option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.description ? `${v.licensePlate} · ${v.description}` : v.licensePlate}
                </option>
              ))}
            </select>
          </label>
        )}

        {role === 'driver' && vehicleId !== '' && (
          <TripDriverFields
            vehicle={vehicle}
            values={driver}
            allowPrivateTrips={allowPrivateTrips}
            onChange={({ purposeKind, ...change }) =>
              setDriver((d) => {
                const next = { ...d, ...change };
                return purposeKind === undefined ? next : withKind(next, purposeKind, filled.current.destination);
              })
            }
          />
        )}
      </div>
    </Modal>
  );
}
